/**
 * useGroups.js — groups, membership and events.
 *
 * Reads are done here; every WRITE goes through a callable, because each one
 * moves a document and a counter together. See functions/groups.js for why
 * that is not negotiable.
 *
 * The group feed is not here either. A group post is an ordinary post with a
 * groupId, so it is fetched by useFeed in usePosts.js with a groupId argument —
 * one feed implementation, one set of pagination bugs already fixed once.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, collectionGroup, doc, query, where, orderBy, limit,
  getDocs, getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { track } from '../lib/tracking';
import { db, functions } from '../firebase';
import { listen } from '../lib/listen';

const callCreateGroup = httpsCallable(functions, 'createGroup');
const callToggleMembership = httpsCallable(functions, 'toggleGroupMembership');
const callDeleteGroup = httpsCallable(functions, 'deleteGroup');
const callCreateEvent = httpsCallable(functions, 'createEvent');
const callSetAttendance = httpsCallable(functions, 'setAttendance');
const callDeleteEvent = httpsCallable(functions, 'deleteEvent');
const callSetPostAlerts = httpsCallable(functions, 'setPostAlerts');
const callSetGroupRole = httpsCallable(functions, 'setGroupRole');
const callTransferGroup = httpsCallable(functions, 'transferGroupOwnership');
const callRemoveMember = httpsCallable(functions, 'removeGroupMember');
const callUnbanMember = httpsCallable(functions, 'unbanGroupMember');
const callRemoveContent = httpsCallable(functions, 'removeGroupContent');

/** Owner outranks moderator outranks member. Mirrors RANK in functions/groups.js. */
export const GROUP_RANK = { member: 0, moderator: 1, owner: 2 };

/**
 * May `myRole` act on someone holding `theirRole`?
 *
 * Only on people strictly below. The server enforces the same rule; this only
 * decides which menu items are worth showing. A non-member (null) ranks below
 * everyone, which covers commenters who never joined.
 */
export function outranks(myRole, theirRole) {
  if (!myRole || GROUP_RANK[myRole] < GROUP_RANK.moderator) return false;
  return GROUP_RANK[myRole] > (theirRole ? GROUP_RANK[theirRole] : -1);
}

/** Remove a group post, or a comment under one. Owner or moderator. */
export function removeGroupContent({ postId, commentId = null }) {
  return callRemoveContent({ postId, commentId });
}

/**
 * Turn post notifications on or off for one person or one group.
 *
 * Exported on its own rather than folded into useGroupActions because the
 * profile page needs it for a person and has no other business with groups.
 */
export function setPostAlerts(kind, targetId, on) {
  return callSetPostAlerts({ kind, targetId, on }).then((r) => r.data?.on === true);
}

/** The colours a group can take, at creation and when its owner edits it. */
/**
 * Group colours carry white initials, so each clears 4.5:1 with white — and
 * they are fixed values, never the accent, so they read the same in dark mode.
 * Amber and pink were #D97706 (3.2:1) and #DB2777 (4.1:1); groups made with
 * those keep working through groupColour().
 */
export const GROUP_COLOURS = ['#1F5D52', '#2563EB', '#7C3AED', '#BE185D', '#B45309', '#0F766E'];

const LEGACY_GROUP_COLOURS = { '#D97706': '#B45309', '#DB2777': '#BE185D' };

/** The colour to paint a group's mark with: legacy values mapped, never the accent. */
export function groupColour(colour) {
  const c = String(colour || '').toUpperCase();
  if (LEGACY_GROUP_COLOURS[c]) return LEGACY_GROUP_COLOURS[c];
  return GROUP_COLOURS.includes(c) ? c : GROUP_COLOURS[0];
}

export const GROUP_NAME_MIN = 3;
export const GROUP_NAME_MAX = 60;
export const GROUP_DESC_MAX = 600;
export const EVENT_TITLE_MAX = 140;
export const EVENT_DESC_MAX = 2000;

/**
 * Must match groupSlug() in functions/groups.js exactly.
 *
 * Duplicated rather than shared because functions/ cannot import from src/ —
 * the same deliberate duplication as lib/profanity.js. If one changes, the
 * client will offer a URL the server does not create, and the group will
 * appear to vanish the moment it is made.
 */
export function groupSlug(name) {
  return String(name || '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/* ── The directory ─────────────────────────────────────────────────────────── */

/**
 * All groups, newest first.
 *
 * A one-shot read, not a subscription. A directory is browsed, not watched, and
 * a listener per visit is a cost with no matching benefit. `nonce` forces a
 * re-read after the viewer creates or joins one.
 */
export function useGroups() {
  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState('loading');
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setStatus('loading');

    getDocs(query(collection(db, 'groups'), orderBy('memberCount', 'desc'), limit(60)))
      .then((snap) => {
        if (!alive) return;
        setGroups(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus(snap.empty ? 'empty' : 'ready');
      })
      .catch((err) => {
        console.error('Could not load groups', err);
        if (alive) setStatus('error');
      });

    return () => { alive = false; };
  }, [nonce]);

  return { groups, status, refresh };
}

/** One group. Live, because the member count moves while you are looking at it. */
export function useGroup(groupId) {
  const [group, setGroup] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!groupId) { setStatus('missing'); return undefined; }

    return listen(
      doc(db, 'groups', groupId),
      (snap) => {
        if (!snap.exists()) { setGroup(null); setStatus('missing'); return; }
        setGroup({ id: snap.id, ...snap.data() });
        setStatus('ready');
      },
      { onFail: () => setStatus('error') },
    );
  }, [groupId]);

  return { group, status };
}

/**
 * Is the signed-in account a member, and at what role?
 *
 * Through listen() rather than a bare onSnapshot for the reason documented in
 * lib/listen.js: a listener reports a permission failure once and never
 * retries, so a transient failure here would render "Join" to someone who is
 * already a member — and pressing it would then remove them.
 */
export function useMyMembership(groupId, uid) {
  const [membership, setMembership] = useState(undefined);   // undefined = not known yet

  useEffect(() => {
    if (!groupId || !uid) { setMembership(null); return undefined; }

    return listen(
      doc(db, 'groups', groupId, 'members', uid),
      (snap) => setMembership(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      // Left as `undefined` on failure, never `null`. The button reads
      // `undefined` as "don't know yet" and stays disabled, which is the only
      // honest thing to show when the answer could be either.
      { onFail: () => setMembership(undefined) },
    );
  }, [groupId, uid]);

  return membership;
}

/**
 * The member list. One-shot; membership churn is not worth a live listener.
 * `nonce` re-reads after a role change or a removal made from this page.
 */
export function useGroupMembers(groupId, max = 50, nonce = 0) {
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!groupId) return undefined;
    let alive = true;

    getDocs(query(
      collection(db, 'groups', groupId, 'members'),
      orderBy('joinedAt', 'asc'),
      limit(max),
    ))
      .then((snap) => {
        if (!alive) return;
        setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus('ready');
      })
      .catch((err) => {
        console.error('Could not load group members', err);
        if (alive) setStatus('error');
      });

    return () => { alive = false; };
  }, [groupId, max, nonce]);

  return { members, status };
}

/**
 * The owner and moderators, as uid → role.
 *
 * Lets the page decide, per post and per comment, whether "Remove" is worth
 * offering: a moderator cannot remove the owner's posts or another
 * moderator's, and offering the button only for the server to refuse it is a
 * small lie. At most twenty-one documents.
 */
export function useGroupStaff(groupId, { enabled = true, nonce = 0 } = {}) {
  const [staff, setStaff] = useState(() => new Map());

  useEffect(() => {
    if (!enabled || !groupId) { setStaff(new Map()); return undefined; }
    let alive = true;
    getDocs(query(collection(db, 'groups', groupId, 'members'), where('role', 'in', ['owner', 'moderator'])))
      .then((snap) => {
        if (alive) setStaff(new Map(snap.docs.map((d) => [d.id, d.data()?.role])));
      })
      .catch((err) => console.warn('Could not load group moderators', err));
    return () => { alive = false; };
  }, [groupId, enabled, nonce]);

  return staff;
}

/**
 * People the group's moderators removed. Only the owner and moderators can
 * read the list, so `enabled` must follow the viewer's role — asking without
 * it is a permission error, not an empty list.
 */
export function useGroupBans(groupId, { enabled = false, nonce = 0 } = {}) {
  const [bans, setBans] = useState([]);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!enabled || !groupId) { setStatus('idle'); return undefined; }
    let alive = true;
    setStatus('loading');

    getDocs(query(collection(db, 'groups', groupId, 'banned'), orderBy('createdAt', 'desc'), limit(200)))
      .then((snap) => {
        if (!alive) return;
        setBans(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus('ready');
      })
      .catch((err) => {
        console.error('Could not load removed members', err);
        if (alive) setStatus('error');
      });

    return () => { alive = false; };
  }, [groupId, enabled, nonce]);

  return { bans, status };
}

/**
 * Was the signed-in account removed from this group?
 *
 * Readable for your own entry only. `undefined` while unknown, like
 * membership, so the page never offers Join and then refuses it.
 */
export function useMyBan(groupId, uid, { enabled = true } = {}) {
  const [banned, setBanned] = useState(undefined);

  useEffect(() => {
    if (!enabled || !groupId || !uid) { setBanned(false); return undefined; }
    let alive = true;
    getDoc(doc(db, 'groups', groupId, 'banned', uid))
      .then((snap) => { if (alive) setBanned(snap.exists()); })
      .catch(() => { if (alive) setBanned(false); });
    return () => { alive = false; };
  }, [groupId, uid, enabled]);

  return banned;
}

/** The moderation log, newest first. Owner and moderators only. */
export function useGroupLog(groupId, { enabled = false, nonce = 0 } = {}) {
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!enabled || !groupId) { setStatus('idle'); return undefined; }
    let alive = true;
    setStatus('loading');

    getDocs(query(collection(db, 'groups', groupId, 'modLog'), orderBy('createdAt', 'desc'), limit(50)))
      .then((snap) => {
        if (!alive) return;
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus('ready');
      })
      .catch((err) => {
        console.error('Could not load the group log', err);
        if (alive) setStatus('error');
      });

    return () => { alive = false; };
  }, [groupId, enabled, nonce]);

  return { entries, status };
}

/**
 * Groups the signed-in account belongs to.
 *
 * `enabled` is not a convenience. This costs one directory read plus one
 * membership read per group in it — around sixty reads — and the events page
 * needs it only to fill a dropdown inside a modal most visitors never open.
 * Firing it on page load would be sixty reads per visit to populate a control
 * nobody looked at.
 */
export function useMyGroups(uid, { enabled = true } = {}) {
  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!enabled) { setStatus('idle'); return undefined; }
    if (!uid) { setStatus('ready'); return undefined; }
    let alive = true;

    (async () => {
      try {
        /**
         * Ask which memberships are MINE, then fetch those groups.
         *
         * The first version of this read the sixty largest groups and checked
         * each one for a membership document. That was wrong, and wrong in the
         * way that does not show up until it matters: a member of a small group
         * outside the top sixty simply would not see it under "Your groups",
         * with nothing on screen to suggest anything was missing. It also cost
         * sixty-one reads to answer a question about, typically, two groups.
         *
         * The collection-group query costs one read per group you are actually
         * in. It was avoided originally over a privacy worry that turns out not
         * to exist: member lists are ALREADY readable per group, so this adds
         * no exposure — and the rule backing it is tighter than the per-group
         * one, permitting only documents whose uid is yours.
         *
         * Needs: the /{path=**}/members/{memberId} rule in firestore.rules, and
         * a COLLECTION_GROUP index on members.uid.
         */
        const mineSnap = await getDocs(
          query(collectionGroup(db, 'members'), where('uid', '==', uid), limit(100))
        );

        const rows = await Promise.all(
          mineSnap.docs.map(async (m) => {
            // groups/{groupId}/members/{uid} → parent is `members`, its parent
            // is the group document.
            const groupRef = m.ref.parent.parent;
            if (!groupRef) return null;
            const g = await getDoc(groupRef);
            return g.exists()
              ? { id: g.id, ...g.data(), myRole: m.data()?.role ?? 'member' }
              : null;
          })
        );

        if (!alive) return;
        setGroups(
          rows.filter(Boolean).sort((a, b) => (b.memberCount ?? 0) - (a.memberCount ?? 0))
        );
        setStatus('ready');
      } catch (err) {
        console.error('Could not load your groups', err);
        if (alive) setStatus('error');
      }
    })();

    return () => { alive = false; };
  }, [uid, enabled]);

  return { groups, status };
}

/* ── Events ────────────────────────────────────────────────────────────────── */

/**
 * Upcoming events, soonest first.
 *
 * Filtered on startsAt >= now so past events fall off by themselves. An events
 * page whose top item finished last March is the clearest possible signal that
 * nobody is using the product.
 */
export function useEvents({ groupId = null, includePast = false } = {}) {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('loading');
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setStatus('loading');

    const clauses = [collection(db, 'events')];
    if (groupId) clauses.push(where('groupId', '==', groupId));
    if (!includePast) clauses.push(where('startsAt', '>=', new Date()));
    clauses.push(orderBy('startsAt', 'asc'), limit(50));

    getDocs(query(...clauses))
      .then((snap) => {
        if (!alive) return;
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus(snap.empty ? 'empty' : 'ready');
      })
      .catch((err) => {
        console.error('Could not load events', err);
        if (alive) setStatus('error');
      });

    return () => { alive = false; };
  }, [groupId, includePast, nonce]);

  return { events, status, refresh };
}

/**
 * My attendance for one event. Same not-known-yet handling as membership.
 *
 * `enabled` off means no listener at all. The compact event rows on a group
 * page render no attendance buttons, so a listener per row there is a live
 * socket per event opened for a value nothing reads.
 */
export function useMyAttendance(eventId, uid, { enabled = true } = {}) {
  const [status, setStatus] = useState(undefined);

  useEffect(() => {
    if (!enabled) { setStatus(undefined); return undefined; }
    if (!eventId || !uid) { setStatus(null); return undefined; }
    return listen(
      doc(db, 'events', eventId, 'attendees', uid),
      (snap) => setStatus(snap.exists() ? (snap.data()?.status ?? 'none') : 'none'),
      { onFail: () => setStatus(undefined) },
    );
  }, [eventId, uid, enabled]);

  return status;
}

/* ── Actions ───────────────────────────────────────────────────────────────── */

/**
 * Every action reports through onError and re-throws.
 *
 * Re-throwing matters: the caller needs to know not to close its form or clear
 * its draft. Swallowing the error and showing a toast is how someone loses a
 * description they spent five minutes writing.
 */
export function useGroupActions({ onError } = {}) {
  const report = useCallback((err, fallback) => {
    console.error(fallback, err);
    onError?.(err?.message || fallback);
  }, [onError]);

  const createGroup = useCallback(async ({ name, description, colour }) => {
    try {
      const res = await callCreateGroup({ name, description, colour });
      return res.data.groupId;
    } catch (err) {
      report(err, 'That group could not be created.');
      throw err;
    }
  }, [report]);

  const toggleMembership = useCallback(async (groupId) => {
    try {
      const res = await callToggleMembership({ groupId });
      if (res.data.member) track('group_join');
      return res.data.member;
    } catch (err) {
      report(err, 'That did not go through.');
      throw err;
    }
  }, [report]);

  const removeGroup = useCallback(async (groupId) => {
    try {
      await callDeleteGroup({ groupId });
    } catch (err) {
      report(err, 'That group could not be deleted.');
      throw err;
    }
  }, [report]);

  const createEvent = useCallback(async (payload) => {
    try {
      const res = await callCreateEvent(payload);
      return res.data.eventId;
    } catch (err) {
      report(err, 'That event could not be created.');
      throw err;
    }
  }, [report]);

  const setAttendance = useCallback(async (eventId, status) => {
    try {
      await callSetAttendance({ eventId, status });
      return status;
    } catch (err) {
      report(err, 'That did not go through.');
      throw err;
    }
  }, [report]);

  const removeEvent = useCallback(async (eventId) => {
    try {
      await callDeleteEvent({ eventId });
    } catch (err) {
      report(err, 'That event could not be deleted.');
      throw err;
    }
  }, [report]);

  const setRole = useCallback(async (groupId, targetId, role) => {
    try {
      await callSetGroupRole({ groupId, targetId, role });
    } catch (err) {
      report(err, 'That role change did not go through.');
      throw err;
    }
  }, [report]);

  const transferOwnership = useCallback(async (groupId, targetId) => {
    try {
      await callTransferGroup({ groupId, targetId });
    } catch (err) {
      report(err, 'The group could not be handed over.');
      throw err;
    }
  }, [report]);

  const removeMember = useCallback(async (groupId, targetId) => {
    try {
      await callRemoveMember({ groupId, targetId });
    } catch (err) {
      report(err, 'They could not be removed.');
      throw err;
    }
  }, [report]);

  const unbanMember = useCallback(async (groupId, targetId) => {
    try {
      await callUnbanMember({ groupId, targetId });
    } catch (err) {
      report(err, 'That did not go through.');
      throw err;
    }
  }, [report]);

  return useMemo(
    () => ({
      createGroup, toggleMembership, removeGroup, createEvent, setAttendance, removeEvent,
      setRole, transferOwnership, removeMember, unbanMember,
    }),
    [createGroup, toggleMembership, removeGroup, createEvent, setAttendance, removeEvent,
      setRole, transferOwnership, removeMember, unbanMember],
  );
}

/* ── Formatting ────────────────────────────────────────────────────────────── */

/** "Thu 2 Oct, 19:00" — or "Thu 2 Oct 2027, 19:00" when it is not this year. */
export function eventWhen(startsAt) {
  const d = startsAt?.toDate ? startsAt.toDate() : (startsAt instanceof Date ? startsAt : null);
  if (!d) return '';

  const thisYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(thisYear ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** True once the event has started. Used to grey a listing rather than hide it. */
export function eventHasStarted(startsAt) {
  const d = startsAt?.toDate ? startsAt.toDate() : (startsAt instanceof Date ? startsAt : null);
  return !!d && d.getTime() < Date.now();
}
